import { Sheet, SheetHeader, SheetTitle, SheetDescription } from '@reflex/ui/components/ui/sheet';
import { VisuallyHidden } from 'radix-ui';
import { ResizableSheetContent } from '@/components/sidebar/ResizableSheetContent';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Bot, Clock, Cpu, ListChecks, MessageSquare, Timer, Wrench } from 'lucide-react';
import { InfoPanel } from '@/components/shared/InfoPanel';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { CopyButton } from '@reflex/ui/components/shared/CopyButton';
import { cn } from '@reflex/ui/lib/utils';
import { AgentStatusChip } from '@/components/status/agent/AgentStatusChip';
import { SandboxStatusBadge } from '@/components/status/SandboxStatusBadge';
import { useAgentPluginContributions } from '@/hooks/config/useAgentPluginContributions';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useAgentAccess } from '@/hooks/agents/useAgentAccess';
import { EditableAgentName } from './EditableAgentName';
import { activityFromDurableAgentState } from '@/hooks/agents/agent-activity-utils';
import { timeAgo, formatDateTimeWithSeconds, formatDuration } from '@reflex/ui/lib/format';
import { useElapsedTime } from '@/hooks/ui/useElapsedTime';
import { TERMINAL_STATUSES } from '@/lib/types';
import type { Agent, AgentStatus } from '@/lib/types';
import { AgentDetailParamsSection } from './agent-detail/AgentDetailParamsSection';
import { AgentDetailIdentity } from './agent-detail/AgentDetailIdentity';
import { AgentDetailInfraSection } from './agent-detail/AgentDetailInfraSection';
import { AgentDetailServersSection } from './agent-detail/AgentDetailServersSection';
import { useAgentActions, AgentActionButtons } from './agent-actions';

function useDuration(createdAt: number, status: AgentStatus) {
  const elapsed = useElapsedTime(createdAt, { stopped: TERMINAL_STATUSES.has(status) });
  return formatDuration(elapsed);
}

const statusGlow: Record<AgentStatus, string> = {
  starting: 'from-status-warning/8 to-transparent',
  running: 'from-status-success/8 to-transparent',
  needs_input: 'from-status-warning/8 to-transparent',
  completed: 'from-status-info/8 to-transparent',
  interrupted: 'from-status-warning/8 to-transparent',
  stopping: 'from-status-accent-orange/8 to-transparent',
  stopped: 'from-status-neutral/5 to-transparent',
  error: 'from-status-error/8 to-transparent',
  terminated: 'from-status-neutral/5 to-transparent',
};

/** Tint for the agent glyph badge in the header — border + fill + icon color. */
const statusAccent: Record<AgentStatus, string> = {
  starting: 'border-status-warning/30 bg-status-warning/10 text-status-warning',
  running: 'border-status-success/30 bg-status-success/10 text-status-success',
  needs_input: 'border-status-warning/30 bg-status-warning/10 text-status-warning',
  completed: 'border-status-info/30 bg-status-info/10 text-status-info',
  interrupted: 'border-status-warning/30 bg-status-warning/10 text-status-warning',
  stopping: 'border-status-accent-orange/30 bg-status-accent-orange/10 text-status-accent-orange',
  stopped: 'border-border bg-muted text-muted-foreground',
  error: 'border-status-error/30 bg-status-error/10 text-status-error',
  terminated: 'border-border bg-muted text-muted-foreground',
};

/** Color of the live "ping" indicator for in-flight statuses (omitted when idle). */
const statusLiveDot: Partial<Record<AgentStatus, string>> = {
  starting: 'bg-status-warning',
  running: 'bg-status-success',
  needs_input: 'bg-status-warning',
};

interface AgentDetailPanelProps {
  agent: Agent | null;
  open: boolean;
  onClose: () => void;
}

export function AgentDetailPanel({ agent, open, onClose }: AgentDetailPanelProps) {
  if (!agent) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={(v: boolean) => {
        if (!v) onClose();
      }}
    >
      <ResizableSheetContent
        defaultWidth={440}
        minWidth={340}
        maxWidth={800}
        storageKey="agent-detail-panel-width"
        // SheetContent is flex-col; overflow-y-auto on it shrinks the header.
        // Scroll one inner block instead (see frontend.md → ResizableSheetContent).
        className="gap-0 overflow-hidden"
      >
        <VisuallyHidden.Root>
          <SheetTitle>Agent details</SheetTitle>
          <SheetDescription>Configuration, status, and controls for this agent.</SheetDescription>
        </VisuallyHidden.Root>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <AgentDetailContent agent={agent} />
        </div>
      </ResizableSheetContent>
    </Sheet>
  );
}

function AgentDetailContent({ agent }: { agent: Agent }) {
  const { agentDetailSections } = usePlugins();
  const { agentActions } = useAgentPluginContributions();
  const agentAccess = useAgentAccess(agent);
  const glow = statusGlow[agent.status];
  const liveDot = statusLiveDot[agent.status];
  const duration = useDuration(agent.createdAt, agent.status);
  // Gate Stop on whether a turn is actually in flight. Derived from the
  // durable turn state, the same rule the sidebar rows and its bulk-select
  // bar use, so this panel never disagrees with the row beside it.
  const activity = activityFromDurableAgentState(agent);
  const actions = useAgentActions(agent, { agentActions, activity });

  return (
    <div>
      <div
        data-testid="agent-detail-header"
        className={cn('relative border-b border-border/60 bg-linear-to-b px-6 pt-6 pb-4', glow)}
      >
        <SheetHeader className="p-0">
          <div className="flex items-start gap-3.5">
            <div className="relative shrink-0" data-testid="agent-detail-header-glyph">
              <div
                className={cn(
                  'flex h-11 w-11 items-center justify-center rounded-xl border shadow-sm',
                  statusAccent[agent.status],
                )}
              >
                <Bot className="h-5 w-5" />
              </div>
              {liveDot && (
                <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
                  <span
                    className={cn(
                      'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60',
                      liveDot,
                    )}
                  />
                  <span
                    className={cn(
                      'relative inline-flex h-3 w-3 rounded-full border-2 border-background',
                      liveDot,
                    )}
                  />
                </span>
              )}
            </div>

            <div className="min-w-0 flex-1">
              {/* Reserve right space for the Sheet's absolute close button
                  (top-4 right-4) so the status cluster never sits under the X,
                  which otherwise collides at narrow widths / high zoom. */}
              <div className="flex items-start justify-between gap-2 pr-7">
                <div className="min-w-0 flex-1">
                  <EditableAgentName
                    agentId={agent.id}
                    name={agent.name}
                    disabled={agentAccess.isReadOnly}
                    as="h2"
                    docs="concepts/agents"
                    truncate={false}
                    className="min-w-0"
                  />
                </div>
                <div
                  className="flex shrink-0 items-center gap-1"
                  data-testid="agent-detail-header-status"
                >
                  <SandboxStatusBadge
                    agentId={agent.id}
                    devboxId={agent.devboxId}
                    agentStatus={agent.status}
                    resourceSize={agent.sandboxOptions?.resourceSize}
                  />
                  <AgentStatusChip status={agent.status} agentId={agent.id} />
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                <Badge
                  variant="outline"
                  className="gap-1 px-1.5 py-0 text-3xs font-mono font-medium"
                >
                  <Cpu className="h-2.5 w-2.5" />
                  {agent.agentType}
                </Badge>
                <span className="inline-flex items-center gap-1 text-2xs text-muted-foreground">
                  <Clock className="h-2.5 w-2.5" />
                  {timeAgo(agent.createdAt)}
                </span>
                <span className="text-muted-foreground/40">&middot;</span>
                <span className="inline-flex items-center gap-1 text-2xs text-muted-foreground">
                  <Timer className="h-2.5 w-2.5" />
                  {duration}
                </span>
              </div>
              <AgentDetailIdentity agent={agent} />
              <div className="mt-2 text-3xs text-muted-foreground/70">
                {formatDateTimeWithSeconds(agent.createdAt)}
              </div>
            </div>
          </div>
        </SheetHeader>
      </div>

      <div
        className="px-6 pt-5 pb-6 space-y-4"
        data-testid="agent-detail-panel"
        data-agent-id={agent.id}
      >
        {/* Actions — same set as the stream header's three-dot menu */}
        <section data-testid="agent-detail-actions">
          <IconSectionHeader
            icon={Wrench}
            className="mb-2 gap-1.5"
            labelAs="h3"
            labelClassName="font-semibold"
          >
            Actions
          </IconSectionHeader>
          <AgentActionButtons actions={actions} agent={agent} />
        </section>

        {/* Prompt */}
        <section data-testid="agent-detail-prompt">
          <IconSectionHeader
            icon={MessageSquare}
            className="mb-2 gap-1.5"
            labelAs="h3"
            labelClassName="font-semibold"
            action={
              agent.prompt ? (
                <CopyButton
                  text={agent.prompt}
                  className="ml-auto h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                />
              ) : undefined
            }
          >
            Prompt
          </IconSectionHeader>
          <InfoPanel className="max-h-40 overflow-y-auto" tabIndex={0}>
            <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
              {agent.prompt || (
                <span className="italic text-muted-foreground">No prompt provided</span>
              )}
            </p>
          </InfoPanel>
        </section>

        {/* System prompt — full text, mirroring the Prompt section. Hidden
            when the launch used the agent type's default instructions. */}
        {agent.systemPrompt ? (
          <section data-testid="agent-detail-system-prompt">
            <IconSectionHeader
              icon={ListChecks}
              className="mb-2 gap-1.5"
              labelAs="h3"
              labelClassName="font-semibold"
              action={
                <CopyButton
                  text={agent.systemPrompt}
                  className="ml-auto h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                />
              }
            >
              System prompt
            </IconSectionHeader>
            <InfoPanel className="max-h-40 overflow-y-auto" tabIndex={0}>
              <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
                {agent.systemPrompt}
              </p>
            </InfoPanel>
          </section>
        ) : null}

        <AgentDetailParamsSection agent={agent} />

        {/* Servers the agent is hosting on its sandbox (hidden when none) */}
        <AgentDetailServersSection agent={agent} />

        {/* Plugin-contributed detail sections */}
        {agentDetailSections.map((section) => (
          <section.component key={section.id} agent={agent} />
        ))}

        <AgentDetailInfraSection agent={agent} />
      </div>
    </div>
  );
}
